import * as React from "react";
import { createContext, useCallback, useContext, useReducer } from "react";
import type { FormApi } from "@/components/slide-panel/types";

/**
 * FormApiContext — TLX-owned context exposing a FormApi to descendants of
 * FormSlidePanel and WizardSlidePanel.
 *
 * Parallel to the legacy `@/context/FormContext` (Kendo-Form-based) which
 * remains used by FormDialog and other pre-RHF code paths.
 * Consumers inside the new RHF-backed slide-panel hierarchy import from here
 * instead.
 */

interface FormApiState {
  fields: Record<string, string | boolean | number>;
  active: number;
  formApi: FormApi;
  isValid?: boolean;
  isLoading?: boolean;
  isEdit?: boolean;
  apiError?: unknown;
  cardFormFields?: string[];
}

type Action =
  | { type: "SET_OPEN"; key: string }
  | { type: "SET_CLOSE"; key: string }
  | { type: "SET_ACTIVE"; key: number }
  | { type: "SET_IS_VALID"; key: string; value: boolean }
  | { type: "SET_IS_LOADING"; key: string; value: boolean }
  | { type: "SET_API_ERROR"; key: string; value: unknown }
  | { type: "SET_IS_EDIT"; key: string; value: boolean }
  | { type: "SET_CARD_FORM_FIELDS"; key: string; value: string[] };

const reducer = (state: FormApiState, action: Action): FormApiState => {
  switch (action.type) {
    case "SET_OPEN":
      return { ...state, fields: { ...state.fields, [action.key]: true } };
    case "SET_CLOSE":
      return { ...state, fields: { ...state.fields, [action.key]: false } };
    case "SET_ACTIVE":
      return { ...state, active: action.key };
    case "SET_IS_VALID":
      return { ...state, isValid: action.value };
    case "SET_IS_LOADING":
      return { ...state, isLoading: action.value };
    case "SET_API_ERROR":
      return { ...state, apiError: action.value };
    case "SET_IS_EDIT":
      return { ...state, isEdit: action.value };
    case "SET_CARD_FORM_FIELDS":
      return {
        ...state,
        cardFormFields: [...(state.cardFormFields ?? []), ...action.value],
      };
    default:
      return state;
  }
};

const FormApiStateContext = createContext<FormApiState | undefined>(undefined);
const FormApiDispatchContext = createContext<
  React.Dispatch<Action> | undefined
>(undefined);

export interface FormApiProviderProps {
  formApi: FormApi;
  fields?: Record<string, string | boolean | number>;
  active?: number;
  isEdit?: boolean;
  children: React.ReactNode;
}

export const FormApiProvider: React.FC<FormApiProviderProps> = ({
  formApi,
  fields = {},
  active = 0,
  isEdit,
  children,
}) => {
  const [state, dispatch] = useReducer(reducer, {
    fields,
    active,
    formApi,
    isEdit,
  });

  // Always expose the latest formApi snapshot so descendants reading
  // .values / .valueGetter / .modified see fresh state.
  const contextValue = React.useMemo(
    () => ({ ...state, formApi }),
    [state, formApi],
  );

  return (
    <FormApiStateContext.Provider value={contextValue}>
      <FormApiDispatchContext.Provider value={dispatch}>
        {children}
      </FormApiDispatchContext.Provider>
    </FormApiStateContext.Provider>
  );
};

/** Returns the FormApi from the surrounding FormApiProvider. */
export function useFormApiCtx<
  TValues extends Record<string, unknown> = Record<string, unknown>,
>(): FormApi<TValues> {
  const ctx = useContext(FormApiStateContext);
  if (!ctx) {
    throw new Error(
      "useFormApiCtx must be used within FormApiProvider (FormSlidePanel or WizardSlidePanel)",
    );
  }
  return ctx.formApi as FormApi<TValues>;
}

/** Returns the full state including reducer-managed fields. */
export const useFormApiState = (): FormApiState => {
  const ctx = useContext(FormApiStateContext);
  if (!ctx) {
    throw new Error("useFormApiState must be used within FormApiProvider");
  }
  return ctx;
};

export const useFormApiDispatch = (): React.Dispatch<Action> => {
  const dispatch = useContext(FormApiDispatchContext);
  if (!dispatch) {
    throw new Error("useFormApiDispatch must be used within FormApiProvider");
  }
  return dispatch;
};

export const useFormApiInvoke = () => {
  const dispatch = useFormApiDispatch();
  return useCallback((action: Action) => dispatch(action), [dispatch]);
};
